import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Archive,
  Lock,
  History,
  Plus,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Calendar,
  TrendingDown,
  ShieldCheck,
  FileText,
  X,
  Check,
  Loader2,
  AlertCircle,
  Eye
} from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import './CloturePeriode.css';

const getApiBase = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  return apiUrl.endsWith('/api') ? apiUrl : apiUrl + '/api';
};
const API_BASE = getApiBase();
const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

const fmt = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' DT';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── Générateur de label auto ─────────────────────────────────────────────────
const genLabel = (type, dateDebut, dateFin) => {
  if (!dateDebut) return '';
  const d = new Date(dateDebut);
  if (type === 'mensuelle') {
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }
  if (type === 'trimestrielle') {
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `T${q} ${d.getFullYear()}`;
  }
  const f = dateFin ? new Date(dateFin) : null;
  return `Période du ${d.toLocaleDateString('fr-FR')} au ${f ? f.toLocaleDateString('fr-FR') : '...'}`;
};

// ─── Étapes du wizard ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Sélection' },
  { id: 2, label: 'Analyse' },
  { id: 3, label: 'Écarts' },
  { id: 4, label: 'Validation' }
];

// ─── Filtre écarts ────────────────────────────────────────────────────────────
const ECART_FILTERS = [
  { id: 'tous', label: 'Tous' },
  { id: 'ecart', label: 'Avec écart' },
  { id: 'non_justifie', label: 'Non justifiés' }
];

export default function CloturePeriode({ onNavigate, onLogout, currentPage }) {
  const [tab, setTab] = useState('nouvelle');
  const [step, setStep] = useState(1);

  // Formulaire nouvelle clôture
  const [form, setForm] = useState({ type: 'mensuelle', dateDebut: '', dateFin: '', label: '' });
  const [analyse, setAnalyse] = useState(null);
  const [clotureEnCours, setClotureEnCours] = useState(null); // brouillon actif

  // Gestion des écarts
  const [ecarts, setEcarts] = useState([]);
  const [ecartFilter, setEcartFilter] = useState('tous');

  // Historique
  const [historique, setHistorique] = useState([]);
  const [selectedCloture, setSelectedCloture] = useState(null);

  const [loading, setLoading] = useState(false);
  const [histLoading, setHistLoading] = useState(false);

  // ─── Auto-complétion dates selon le type ─────────────────────────────────
  useEffect(() => {
    if (!form.type) return;
    const today = new Date();
    let dDebut, dFin;

    if (form.type === 'mensuelle') {
      dDebut = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      dFin = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (form.type === 'trimestrielle') {
      const q = Math.floor(today.getMonth() / 3);
      const prevQ = q === 0 ? 3 : q - 1;
      const year = q === 0 ? today.getFullYear() - 1 : today.getFullYear();
      dDebut = new Date(year, prevQ * 3, 1);
      dFin = new Date(year, prevQ * 3 + 3, 0);
    } else {
      return;
    }

    const toStr = (d) => d.toISOString().split('T')[0];
    setForm(f => ({
      ...f,
      dateDebut: toStr(dDebut),
      dateFin: toStr(dFin),
      label: genLabel(form.type, toStr(dDebut), toStr(dFin))
    }));
  }, [form.type]);

  // ─── Historique ──────────────────────────────────────────────────────────
  const chargerHistorique = useCallback(async () => {
    setHistLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/clotures-periodes`, authHeader());
      setHistorique(data);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'historique') chargerHistorique();
  }, [tab, chargerHistorique]);

  // ─── Étape 1 → 2 : Analyse ───────────────────────────────────────────────
  const lancerAnalyse = async () => {
    if (!form.dateDebut || !form.dateFin) {
      Swal.fire({ icon: 'warning', title: 'Dates manquantes', text: 'Veuillez sélectionner une date de début et de fin.', background: '#1e293b', color: '#e2e8f0' });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/clotures-periodes/analyser`, {
        dateDebut: form.dateDebut,
        dateFin: form.dateFin
      }, authHeader());

      setAnalyse(data);
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de l\'analyse';
      Swal.fire({ icon: 'error', title: 'Erreur', text: msg, background: '#1e293b', color: '#e2e8f0' });
    } finally {
      setLoading(false);
    }
  };

  // ─── Étape 2 → 3 : Initier la clôture et passer aux écarts ──────────────
  const initierCloture = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/clotures-periodes`, {
        type: form.type,
        dateDebut: form.dateDebut,
        dateFin: form.dateFin,
        label: form.label || genLabel(form.type, form.dateDebut, form.dateFin)
      }, authHeader());

      setClotureEnCours(data);
      setEcarts(data.snapshotStock.map(s => ({
        produitId: s.produitId?._id || s.produitId,
        nomProduit: s.nomProduit,
        reference: s.reference,
        quantiteTheorique: s.quantiteTheorique,
        quantiteReelle: s.quantiteReelle !== null ? s.quantiteReelle : s.quantiteTheorique,
        ecart: s.ecart,
        ecartJustifie: s.ecartJustifie,
        justification: s.justification || ''
      })));
      setStep(3);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la création de la clôture';
      Swal.fire({ icon: 'error', title: 'Erreur', text: msg, background: '#1e293b', color: '#e2e8f0' });
    } finally {
      setLoading(false);
    }
  };

  // ─── Mise à jour locale des écarts ───────────────────────────────────────
  const updateEcart = (produitId, field, value) => {
    setEcarts(prev => prev.map(e => {
      if (e.produitId !== produitId) return e;
      const updated = { ...e, [field]: value };
      if (field === 'quantiteReelle') {
        updated.quantiteReelle = value === '' ? null : Number(value);
        updated.ecart = (updated.quantiteReelle ?? e.quantiteTheorique) - e.quantiteTheorique;
        if (updated.ecart === 0) updated.ecartJustifie = true;
      }
      return updated;
    }));
  };

  // ─── Sauvegarder les écarts → Étape 4 ────────────────────────────────────
  const sauvegarderEcarts = async () => {
    setLoading(true);
    try {
      await axios.patch(
        `${API_BASE}/clotures-periodes/${clotureEnCours._id}/ecarts`,
        { ecarts },
        authHeader()
      );
      setStep(4);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la sauvegarde';
      Swal.fire({ icon: 'error', title: 'Erreur', text: msg, background: '#1e293b', color: '#e2e8f0' });
    } finally {
      setLoading(false);
    }
  };

  // ─── Validation finale ────────────────────────────────────────────────────
  const validerCloture = async () => {
    const nonJustifies = ecarts.filter(e => e.ecart !== 0 && !e.ecartJustifie);
    if (nonJustifies.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: `${nonJustifies.length} écart(s) non justifié(s)`,
        text: 'Veuillez justifier tous les écarts de stock avant de valider.',
        background: '#1e293b', color: '#e2e8f0'
      });
      setStep(3);
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'Confirmer la clôture ?',
      html: `<p style="color:#94a3b8">Cette action va <strong style="color:#f1f5f9">verrouiller définitivement</strong> toutes les données de la période <strong style="color:#a5b4fc">${clotureEnCours.label}</strong>.<br><br>Aucune modification ne sera possible sur cette période.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Valider la clôture',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#475569',
      background: '#1e293b', color: '#e2e8f0'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/clotures-periodes/${clotureEnCours._id}/valider`, {
        commentaire: 'Validation finale par l\'administrateur'
      }, authHeader());

      await Swal.fire({
        icon: 'success',
        title: 'Clôture validée !',
        text: `La période ${clotureEnCours.label} est maintenant archivée et verrouillée.`,
        background: '#1e293b', color: '#e2e8f0', confirmButtonColor: '#16a34a'
      });

      resetForm();
      setTab('historique');
      chargerHistorique();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la validation';
      Swal.fire({ icon: 'error', title: 'Erreur', text: msg, background: '#1e293b', color: '#e2e8f0' });
    } finally {
      setLoading(false);
    }
  };

  // ─── Annuler la clôture en cours ─────────────────────────────────────────
  const annulerClotureEnCours = async () => {
    if (!clotureEnCours) { resetForm(); return; }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Annuler ce brouillon ?',
      text: 'Le brouillon de clôture sera annulé. Aucune donnée ne sera verrouillée.',
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Conserver',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#475569',
      background: '#1e293b', color: '#e2e8f0'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(`${API_BASE}/clotures-periodes/${clotureEnCours._id}/annuler`, {
        motif: 'Annulation par l\'administrateur'
      }, authHeader());
      resetForm();
    } catch (err) {
      console.error('Erreur annulation:', err);
      resetForm();
    }
  };

  // ─── Annuler une clôture depuis l'historique ──────────────────────────────
  const annulerDepuisHistorique = async (cloture) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Annuler ce brouillon ?',
      text: `Annuler "${cloture.label}" ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non',
      confirmButtonColor: '#dc2626',
      background: '#1e293b', color: '#e2e8f0'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(`${API_BASE}/clotures-periodes/${cloture._id}/annuler`, {
        motif: 'Annulé depuis l\'historique'
      }, authHeader());
      chargerHistorique();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message || 'Erreur', background: '#1e293b', color: '#e2e8f0' });
    }
  };

  const resetForm = () => {
    setStep(1);
    setAnalyse(null);
    setClotureEnCours(null);
    setEcarts([]);
    setEcartFilter('tous');
    setForm({ type: 'mensuelle', dateDebut: '', dateFin: '', label: '' });
  };

  // ─── Filtrage des écarts ──────────────────────────────────────────────────
  const ecartsAffiches = ecarts.filter(e => {
    if (ecartFilter === 'ecart') return e.ecart !== 0;
    if (ecartFilter === 'non_justifie') return e.ecart !== 0 && !e.ecartJustifie;
    return true;
  });
  const nbEcartsNonJustifies = ecarts.filter(e => e.ecart !== 0 && !e.ecartJustifie).length;

  // ─── Rendu Stepper ───────────────────────────────────────────────────────
  const renderStepper = () => (
    <div className="cloture-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div className={`step-item ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`}>
            <div className="step-circle">
              {step > s.id ? <Check size={14} /> : s.id}
            </div>
            <span className="step-label">{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className="step-connector" />}
        </React.Fragment>
      ))}
    </div>
  );

  // ─── Étape 1 : Sélection période ─────────────────────────────────────────
  const renderStep1 = () => (
    <div className="cloture-card">
      <h2><Calendar size={18} /> Définir la période à clôturer</h2>

      <div className="cloture-alert info">
        <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Une fois validée, toutes les données de la période seront archivées en lecture seule. Les modifications sur inventaire, dépenses et factures seront bloquées pour cette période.</span>
      </div>

      <div className="periode-form">
        <div className="form-group">
          <label>Type de clôture</label>
          <select
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
          >
            <option value="mensuelle">Mensuelle</option>
            <option value="trimestrielle">Trimestrielle</option>
            <option value="personnalisee">Personnalisée</option>
          </select>
        </div>

        <div className="form-group">
          <label>Date de début</label>
          <input
            type="date"
            value={form.dateDebut}
            onChange={e => {
              const v = e.target.value;
              setForm(f => ({
                ...f,
                dateDebut: v,
                label: genLabel(f.type, v, f.dateFin)
              }));
            }}
          />
        </div>

        <div className="form-group">
          <label>Date de fin</label>
          <input
            type="date"
            value={form.dateFin}
            min={form.dateDebut}
            onChange={e => {
              const v = e.target.value;
              setForm(f => ({
                ...f,
                dateFin: v,
                label: genLabel(f.type, f.dateDebut, v)
              }));
            }}
          />
        </div>

        <div className="form-group">
          <label>Label (optionnel)</label>
          <input
            type="text"
            value={form.label}
            placeholder={genLabel(form.type, form.dateDebut, form.dateFin)}
            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
          />
        </div>
      </div>

      <div className="btn-actions">
        <button className="btn-primary" onClick={lancerAnalyse} disabled={loading || !form.dateDebut || !form.dateFin}>
          {loading ? <Loader2 size={16} className="spin" /> : <BarChart3 size={16} />}
          Analyser la période
        </button>
      </div>
    </div>
  );

  // ─── Étape 2 : Résultat de l'analyse ─────────────────────────────────────
  const renderStep2 = () => (
    <>
      <div className="cloture-card">
        <h2><BarChart3 size={18} /> Résumé de la période — {form.label || genLabel(form.type, form.dateDebut, form.dateFin)}</h2>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Dépenses</div>
            <div className="stat-value">{fmt(analyse.totauxFinanciers.totalDepenses)}</div>
            <div className="stat-sub">{analyse.statistiques.nbDepenses} opération(s)</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Factures fournisseurs</div>
            <div className="stat-value">{fmt(analyse.totauxFinanciers.totalFacturesFournisseurs)}</div>
            <div className="stat-sub">{analyse.statistiques.nbFacturesFournisseurs} facture(s)</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-label">Factures clients</div>
            <div className="stat-value">{fmt(analyse.totauxFinanciers.totalFacturesClients)}</div>
            <div className="stat-sub">{analyse.statistiques.nbDocumentsFinanciers} document(s)</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Mouvements stock</div>
            <div className="stat-value">{analyse.statistiques.nbMouvementsStock}</div>
            <div className="stat-sub">ajustements</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Produits à vérifier</div>
            <div className="stat-value">{analyse.snapshotStock?.length || 0}</div>
            <div className="stat-sub">références</div>
          </div>
        </div>

        {analyse.snapshotStock?.length > 0 && (
          <div className="cloture-alert warning">
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>L'étape suivante vous permettra de saisir les quantités réelles de stock et de justifier les écarts détectés avant la validation finale.</span>
          </div>
        )}
      </div>

      <div className="btn-actions">
        <button className="btn-ghost" onClick={() => setStep(1)}>
          <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
          Modifier la période
        </button>
        <button className="btn-primary" onClick={initierCloture} disabled={loading}>
          {loading ? <Loader2 size={16} className="spin" /> : <ChevronRight size={16} />}
          Passer à la vérification des écarts
        </button>
      </div>
    </>
  );

  // ─── Étape 3 : Gestion des écarts de stock ────────────────────────────────
  const renderStep3 = () => (
    <>
      <div className="cloture-card">
        <h2>
          <TrendingDown size={18} />
          Vérification du stock — {clotureEnCours?.label}
          {nbEcartsNonJustifies > 0 && (
            <span className="statut-badge en_validation" style={{ marginLeft: 'auto' }}>
              {nbEcartsNonJustifies} écart(s) non justifié(s)
            </span>
          )}
        </h2>

        <div className="cloture-alert info" style={{ marginBottom: 14 }}>
          <AlertCircle size={18} style={{ flexShrink: 0 }} />
          <span>Saisissez la quantité réelle pour chaque produit. Si un écart est détecté (quantité réelle ≠ théorique), vous devez le justifier pour pouvoir valider la clôture.</span>
        </div>

        <div className="ecarts-filter-bar">
          {ECART_FILTERS.map(f => (
            <button
              key={f.id}
              className={`filter-chip ${ecartFilter === f.id ? 'active' : ''}`}
              onClick={() => setEcartFilter(f.id)}
            >
              {f.label}
              {f.id === 'non_justifie' && nbEcartsNonJustifies > 0 && (
                <span style={{ marginLeft: 6, background: 'rgba(239,68,68,0.25)', color: '#f87171', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>
                  {nbEcartsNonJustifies}
                </span>
              )}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
            {ecartsAffiches.length} / {ecarts.length} produit(s)
          </span>
        </div>

        <div className="ecarts-table-wrapper">
          <table className="ecarts-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Réf.</th>
                <th>Qté théorique</th>
                <th>Qté réelle</th>
                <th>Écart</th>
                <th>Justification</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {ecartsAffiches.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#64748b', padding: '30px' }}>
                    Aucun produit à afficher avec ce filtre
                  </td>
                </tr>
              ) : ecartsAffiches.map(e => (
                <tr key={e.produitId}>
                  <td style={{ fontWeight: 600, color: '#f1f5f9' }}>{e.nomProduit}</td>
                  <td style={{ color: '#64748b', fontSize: 12 }}>{e.reference || '—'}</td>
                  <td style={{ fontWeight: 600 }}>{e.quantiteTheorique}</td>
                  <td>
                    <input
                      type="number"
                      className="ecart-input"
                      min="0"
                      step="0.001"
                      value={e.quantiteReelle !== null ? e.quantiteReelle : ''}
                      placeholder={String(e.quantiteTheorique)}
                      onChange={ev => updateEcart(e.produitId, 'quantiteReelle', ev.target.value)}
                    />
                  </td>
                  <td>
                    <span className={`ecart-badge ${e.ecart === 0 ? 'zero' : e.ecart > 0 ? 'positif' : 'negatif'}`}>
                      {e.ecart > 0 ? '+' : ''}{e.ecart !== 0 ? e.ecart : '—'}
                    </span>
                  </td>
                  <td>
                    {e.ecart !== 0 ? (
                      <input
                        type="text"
                        className="justif-input"
                        value={e.justification}
                        placeholder="Expliquer l'écart..."
                        onChange={ev => updateEcart(e.produitId, 'justification', ev.target.value)}
                      />
                    ) : (
                      <span style={{ color: '#475569', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    {e.ecart !== 0 ? (
                      <button
                        className={`justif-toggle ${e.ecartJustifie ? 'justified' : ''}`}
                        onClick={() => updateEcart(e.produitId, 'ecartJustifie', !e.ecartJustifie)}
                        title={e.ecartJustifie ? 'Cliquer pour marquer non justifié' : 'Marquer comme justifié'}
                      >
                        {e.ecartJustifie ? <Check size={12} /> : <X size={12} />}
                        {e.ecartJustifie ? 'Justifié' : 'À justifier'}
                      </button>
                    ) : (
                      <span style={{ color: '#4ade80', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Check size={12} /> OK
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="btn-actions">
        <button className="btn-danger" onClick={annulerClotureEnCours}>
          <X size={15} /> Annuler ce brouillon
        </button>
        <button
          className="btn-primary"
          onClick={sauvegarderEcarts}
          disabled={loading || nbEcartsNonJustifies > 0}
          title={nbEcartsNonJustifies > 0 ? 'Justifiez tous les écarts avant de continuer' : ''}
        >
          {loading ? <Loader2 size={16} className="spin" /> : <ChevronRight size={16} />}
          Continuer vers la validation
          {nbEcartsNonJustifies > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>({nbEcartsNonJustifies} restant)</span>}
        </button>
      </div>
    </>
  );

  // ─── Étape 4 : Validation finale ─────────────────────────────────────────
  const renderStep4 = () => {
    const totalEcarts = ecarts.filter(e => e.ecart !== 0).length;
    const totalProduits = ecarts.length;
    const { totauxFinanciers, statistiques } = analyse || clotureEnCours || {};

    return (
      <>
        <div className="rapport-section">
          <h3><FileText size={18} /> Rapport de clôture — {clotureEnCours?.label}</h3>
          <div className="rapport-grid">
            <div className="rapport-item">
              <span className="rl">Période</span>
              <span className="rv">{fmtDate(clotureEnCours?.dateDebut)} → {fmtDate(clotureEnCours?.dateFin)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Total dépenses</span>
              <span className="rv">{fmt(totauxFinanciers?.totalDepenses)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Total factures fournisseurs</span>
              <span className="rv">{fmt(totauxFinanciers?.totalFacturesFournisseurs)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Chiffre d'affaires clients</span>
              <span className="rv">{fmt(totauxFinanciers?.totalFacturesClients)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Mouvements de stock</span>
              <span className="rv">{statistiques?.nbMouvementsStock || 0}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Produits vérifiés</span>
              <span className="rv">{totalProduits}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Écarts détectés</span>
              <span className="rv" style={{ color: totalEcarts > 0 ? '#fbbf24' : '#4ade80' }}>{totalEcarts}</span>
            </div>
          </div>
        </div>

        <div className="cloture-card" style={{ marginTop: 20 }}>
          <h2><ShieldCheck size={18} /> Confirmation avant verrouillage</h2>

          <div className="cloture-alert warning">
            <AlertTriangle size={18} style={{ flexShrink: 0 }} />
            <span>
              <strong>Action irréversible.</strong> En validant, toutes les données de la période <strong>{clotureEnCours?.label}</strong> seront archivées en lecture seule. Aucune dépense, facture ou mouvement ne pourra être créé ou modifié sur cette période.
            </span>
          </div>

          {totalEcarts > 0 && (
            <div className="cloture-alert success">
              <CheckCircle2 size={18} style={{ flexShrink: 0 }} />
              <span>Tous les {totalEcarts} écarts de stock ont été justifiés. La clôture peut être finalisée.</span>
            </div>
          )}

          <div className="btn-actions">
            <button className="btn-ghost" onClick={() => setStep(3)}>
              <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Revenir aux écarts
            </button>
            <button className="btn-danger" onClick={annulerClotureEnCours}>
              <X size={15} /> Annuler le brouillon
            </button>
            <button className="btn-success" onClick={validerCloture} disabled={loading}>
              {loading ? <Loader2 size={16} className="spin" /> : <Lock size={16} />}
              Valider et clôturer la période
            </button>
          </div>
        </div>
      </>
    );
  };

  // ─── Onglet Historique ────────────────────────────────────────────────────
  const renderHistorique = () => {
    if (histLoading) return (
      <div className="cloture-loader">
        <Loader2 size={32} className="spin" />
        Chargement de l'historique...
      </div>
    );

    if (historique.length === 0) return (
      <div className="cloture-empty">
        <Archive size={48} />
        <p>Aucune clôture enregistrée</p>
        <span>Les clôtures validées apparaîtront ici.</span>
      </div>
    );

    return (
      <div className="historique-list">
        {historique.map(c => (
          <div key={c._id} className="historique-item">
            <div>
              <div className="historique-item-title">
                <Archive size={16} style={{ color: '#6366f1' }} />
                {c.label || `Période du ${fmtDate(c.dateDebut)} au ${fmtDate(c.dateFin)}`}
                <span className={`statut-badge ${c.statut}`}>{c.statut.replace('_', ' ')}</span>
              </div>
              <div className="historique-item-meta">
                <span><Calendar size={13} /> {fmtDate(c.dateDebut)} → {fmtDate(c.dateFin)}</span>
                <span><FileText size={13} /> {c.type}</span>
                {c.creePar?.nom && (
                  <span>Créée par {c.creePar.prenom} {c.creePar.nom}</span>
                )}
                {c.validePar?.nom && (
                  <span><CheckCircle2 size={13} color="#4ade80" /> Validée par {c.validePar.prenom} {c.validePar.nom} le {fmtDate(c.validePar?.date)}</span>
                )}
              </div>
              {c.statut === 'validee' && (
                <div style={{ marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    Dépenses : <strong style={{ color: '#e2e8f0' }}>{fmt(c.totauxFinanciers?.totalDepenses)}</strong>
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    FF : <strong style={{ color: '#e2e8f0' }}>{fmt(c.totauxFinanciers?.totalFacturesFournisseurs)}</strong>
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    CA : <strong style={{ color: '#e2e8f0' }}>{fmt(c.totauxFinanciers?.totalFacturesClients)}</strong>
                  </span>
                </div>
              )}
            </div>
            <div className="historique-actions">
              {c.statut === 'validee' && (
                <button
                  className="btn-ghost"
                  style={{ padding: '7px 14px', fontSize: 12 }}
                  onClick={() => setSelectedCloture(c)}
                >
                  <Eye size={13} /> Détail
                </button>
              )}
              {(c.statut === 'brouillon' || c.statut === 'en_validation') && (
                <button
                  className="btn-danger"
                  style={{ padding: '7px 14px', fontSize: 12 }}
                  onClick={() => annulerDepuisHistorique(c)}
                >
                  <X size={13} /> Annuler
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Modal détail clôture ─────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selectedCloture) return null;
    const c = selectedCloture;
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20
      }} onClick={() => setSelectedCloture(null)}>
        <div
          style={{
            background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 18, padding: 32, maxWidth: 640, width: '100%',
            maxHeight: '80vh', overflowY: 'auto'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#f1f5f9', display: 'flex', gap: 10, alignItems: 'center' }}>
              <Archive size={20} color="#6366f1" />
              {c.label}
            </h2>
            <button style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }} onClick={() => setSelectedCloture(null)}>
              <X size={20} />
            </button>
          </div>

          <div className="rapport-grid" style={{ marginBottom: 20 }}>
            <div className="rapport-item">
              <span className="rl">Période</span>
              <span className="rv">{fmtDate(c.dateDebut)} → {fmtDate(c.dateFin)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Total dépenses</span>
              <span className="rv">{fmt(c.totauxFinanciers?.totalDepenses)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">Factures fournisseurs</span>
              <span className="rv">{fmt(c.totauxFinanciers?.totalFacturesFournisseurs)}</span>
            </div>
            <div className="rapport-item">
              <span className="rl">CA clients</span>
              <span className="rv">{fmt(c.totauxFinanciers?.totalFacturesClients)}</span>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Historique des actions</div>
            {(c.historique || []).map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 13 }}>
                <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(h.date)}</span>
                <span style={{ color: '#94a3b8' }}><strong style={{ color: '#e2e8f0' }}>{h.prenom} {h.nom}</strong> — {h.commentaire}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ─── Rendu principal ──────────────────────────────────────────────────────
  return (
    <div className="cloture-page">
      <BackOfficeMenu currentPage={currentPage} onNavigate={onNavigate} onLogout={onLogout} />

      <div className="cloture-content">
        <div className="cloture-header">
          <div className="cloture-header-icon">
            <Archive size={24} color="#fff" />
          </div>
          <div>
            <h1>Clôture de Période</h1>
            <p>Archivage comptable et opérationnel — inventaire, dépenses, factures</p>
          </div>
        </div>

        <div className="cloture-tabs">
          <button className={`cloture-tab ${tab === 'nouvelle' ? 'active' : ''}`} onClick={() => setTab('nouvelle')}>
            <Plus size={15} /> Nouvelle Clôture
          </button>
          <button className={`cloture-tab ${tab === 'historique' ? 'active' : ''}`} onClick={() => setTab('historique')}>
            <History size={15} /> Historique
            {historique.filter(c => c.statut === 'validee').length > 0 && (
              <span style={{ marginLeft: 6, background: 'rgba(34,197,94,0.2)', color: '#4ade80', borderRadius: '50%', padding: '0 6px', fontSize: 11, fontWeight: 700 }}>
                {historique.filter(c => c.statut === 'validee').length}
              </span>
            )}
          </button>
        </div>

        {tab === 'nouvelle' && (
          <>
            {renderStepper()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </>
        )}

        {tab === 'historique' && renderHistorique()}
      </div>

      {renderDetailModal()}
    </div>
  );
}
